import { useState } from "react";
import { apiClient } from "../../lib/api";
import type { WorksPlacement } from "./WorksPlacementManager";

interface WorksPlacementCardProps {
  placement: WorksPlacement;
  imageUrl: string | undefined;
  onDelete: (id: string | number) => Promise<void>;
  onUpdate: () => Promise<void>;
  maxOrder: number;
  workPlacements: WorksPlacement[];
}

export function WorksPlacementCard({
  placement,
  imageUrl,
  onDelete,
  onUpdate,
  maxOrder,
  workPlacements,
}: WorksPlacementCardProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [displayOrder, setdisplayOrder] = useState(placement.display_order);
  const [isVisible, setIsVisible] = useState(placement.is_visible);
  const [isUpdating, setIsUpdating] = useState(false);

  const handleOrderChange = async (newOrder: number) => {
    if (newOrder === placement.display_order) {
      setIsEditing(false);
      return;
    }

    setIsUpdating(true);
    try {
      // 同じグループ内の他の画像の順序を調整
      const updates = workPlacements
        .map((p) => {
          if (p.id === placement.id) {
            // 対象の画像の順序を更新
            return {
              id: p.id,
              display_order: newOrder,
            };
          } else if (
            // 上に移動する場合
            newOrder > placement.display_order &&
            p.display_order > placement.display_order &&
            p.display_order <= newOrder
          ) {
            return {
              id: p.id,
              display_order: p.display_order - 1,
            };
          } else if (
            // 下に移動する場合
            newOrder < placement.display_order &&
            p.display_order >= newOrder &&
            p.display_order < placement.display_order
          ) {
            return {
              id: p.id,
              display_order: p.display_order + 1,
            };
          }
          return null;
        })
        .filter(
          (update): update is { id: number; display_order: number } =>
            update !== null
        );

      // 一括更新
      for (const update of updates) {
        await apiClient.patch(`/works_placements/${update.id}`, {
          display_order: update.display_order
        });
      }

      await onUpdate();
      setIsEditing(false);
    } catch (error) {
      console.error("順序の更新に失敗しました:", error);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleVisibilityChange = async (newVisibility: boolean) => {
    setIsUpdating(true);
    try {
      await apiClient.patch(`/works_placements/${placement.id}`, {
        is_visible: newVisibility
      });

      setIsVisible(newVisibility);
      await onUpdate();
    } catch (error) {
      console.error("表示状態の更新に失敗しました:", error);
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <div className="flex items-center justify-between bg-white p-4 rounded-lg shadow-sm">
      <div className="flex items-center space-x-4">
        <div className="w-16 h-16 bg-gray-100 rounded overflow-hidden">
          {placement.illustration.storage_path && imageUrl && (
            <img
              src={imageUrl}
              alt={placement.illustration.title}
              className="w-full h-full object-cover"
            />
          )}
        </div>
        <div>
          <div className="font-medium">{placement.illustration.title}</div>
          <div className="text-sm text-gray-500 flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <span>表示順序:</span>
              {isEditing ? (
                <select
                  value={displayOrder}
                  onChange={(e) => {
                    const newOrder = parseInt(e.target.value);
                    setdisplayOrder(newOrder);
                    handleOrderChange(newOrder);
                  }}
                  className="border rounded px-2 py-1 text-sm"
                  disabled={isUpdating}
                >
                  {Array.from({ length: maxOrder + 1 }, (_, i) => (
                    <option key={i} value={i}>
                      {i}
                    </option>
                  ))}
                </select>
              ) : (
                <button
                  onClick={() => setIsEditing(true)}
                  className="text-blue-600 hover:text-blue-700"
                >
                  {placement.display_order}
                </button>
              )}
            </div>
            <div className="flex items-center space-x-2">
              <span>表示:</span>
              <input
                type="checkbox"
                checked={isVisible}
                onChange={(e) => handleVisibilityChange(e.target.checked)}
                className="rounded border-gray-300 text-indigo-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                disabled={isUpdating}
              />
            </div>
          </div>
        </div>
      </div>
      <div className="flex gap-2">
        <button
          onClick={() => onDelete(placement.id)}
          className="px-3 py-1 text-sm text-red-600 bg-red-100 rounded hover:bg-red-200"
          disabled={isUpdating}
        >
          削除
        </button>
      </div>
    </div>
  );
}
